import React, { useState, useRef, useEffect, useCallback } from "react"
import { twMerge } from "tailwind-merge"

import { getTextColor } from "../../../utils/shared-utils"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { getThemeShapeClasses } from "../utils/themeUtils"
import { scopePath } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const ReviewsSectionMobile = ({ section, practice, theme, highlightedField, pageSlug = null }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}

  // Local state for reviews and load more functionality
  const [allReviews, setAllReviews] = useState(practice?.reviews || [])
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [hasMoreReviews, setHasMoreReviews] = useState(true)

  // Ref for infinite scroll sentinel
  const sentinelRef = useRef(null)

  // Sync initial state with props
  useEffect(() => {
    if (practice?.reviews && practice.reviews.length > 0 && allReviews.length === 0) {
      setAllReviews(practice.reviews)
    }
  }, [practice?.reviews])

  // Infinite scroll hook
  const handleIntersection = useCallback(
    (entries) => {
      const target = entries[0]
      if (target.isIntersecting && hasMoreReviews && !isLoading) {
        loadMoreReviews()
      }
    },
    [hasMoreReviews, isLoading]
  )

  // Set up Intersection Observer
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: "100px", // Start loading 100px before the sentinel is visible
      threshold: 0.1
    })

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [handleIntersection])

  // Function to load more reviews
  const loadMoreReviews = async () => {
    if (isLoading || !hasMoreReviews) return

    setIsLoading(true)
    try {
      // Make API call to get more reviews
      const url = `/practices/${practice.id}/reviews?page=${currentPage + 1}&per_page=10`

      const response = await fetch(url)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      if (data.reviews && data.reviews.length > 0) {
        // Add new reviews to existing ones
        setAllReviews((prevReviews) => [...prevReviews, ...data.reviews])
        setCurrentPage((prev) => prev + 1)

        // Check if there are more reviews to load
        if (data.reviews.length < 10) {
          setHasMoreReviews(false)
        }
      } else {
        setHasMoreReviews(false)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error loading more reviews:", error)
    } finally {
      setIsLoading(false)
    }
  }

  // Check if we're currently on the home page
  const isOnHomePage = pageSlug === "home"

  // Check if we're currently on the reviews page
  const isOnReviewsPage = pageSlug === "review" || window.location.pathname === "/review"

  const showRatings = settings.showRatings || true
  const selectedReviewIds = settings.selectedReviewIds || []
  const showDates = settings.showDates || true
  const showOverallRating = settings.showOverallRating || true
  const showCTA = settings.showCTA || true
  const ctaText = settings.ctaText || "View all reviews"
  const layout = settings.layout || "cards"

  // Get theme colors with fallback generation for bgMedium and bgExtraLight
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    // If bgMedium or bgExtraLight are missing, generate them
    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()

  // Resolve background color based on toggle setting
  const resolveSectionBackgroundColor = () => {
    // Check if background color toggle is enabled
    if (settings.showBackgroundColor && settings.backgroundColor === "theme") {
      // Use alert yellow if bgLight is missing to surface misconfiguration
      return themeColors.bgLight || "#FFF200"
    }
    // Default to white if toggle is off or not set
    return "#FFFFFF"
  }

  const backgroundColor = resolveSectionBackgroundColor()

  // Video review settings
  const showVideoReviews = settings.showVideoReviews !== false // Default to true
  const videoReviewsCount = settings.videoReviewsCount || 3
  const selectedVideoReviewIds = settings.selectedVideoReviewIds || []

  // Text review settings
  const showTextReviews = settings.showTextReviews !== false // Default to true
  const textReviewsCount = settings.textReviewsCount || 3

  // Get text reviews (filter out those with video reviews)
  const allTextReviews = (allReviews || []).filter((review) => !review.hasCompletedVideoReview)

  const selectedTextReviews = isOnHomePage
    ? selectedReviewIds.length > 0
      ? allTextReviews.filter((review) => selectedReviewIds.includes(review.id))
      : allTextReviews.slice(0, textReviewsCount) // Limit on home page
    : allTextReviews // Show all text reviews when not on home page

  // Get video reviews
  const allVideoReviews = practice?.videoReviews || []
  const selectedVideoReviews = isOnHomePage
    ? selectedReviewIds.length > 0
      ? allVideoReviews.filter((review) => selectedVideoReviewIds.includes(review.id))
      : allVideoReviews.slice(0, videoReviewsCount) // Limit on home page
    : allVideoReviews // Show all video reviews when not on home page

  // Combined reviews for overall rating (use all reviews including those with video reviews)
  // allReviews is now managed by local state

  const averageRating =
    allReviews.length > 0
      ? (
          allReviews.reduce((sum, review) => {
            // Try different possible rating field names
            const rating = review.rating || review.score || review.stars || review.ratingValue || 0
            return sum + (rating || 0)
          }, 0) / allReviews.length
        ).toFixed(1)
      : "0.0"

  const VideoReviewCard = ({ videoReview, index }) => {
    const [isMuted, setIsMuted] = useState(true)
    const videoRef = useRef(null)

    // Safety checks for data
    if (!videoReview || !videoReview.id) return null

    // Get client name and anonymization
    const clientName = videoReview.client?.firstName || "Anonymous"
    const clientLastName = videoReview.client?.lastName || ""
    const displayName = clientName !== "Anonymous" ? `${clientName} ${clientLastName[0] || ""}.` : "Anonymous"

    // Get rating from review if it exists
    const rating = videoReview.review?.rating || 5

    // Get date - use review date if available, otherwise video review date
    const reviewDate = videoReview.review?.createdAt || videoReview.createdAt
    const formattedDate = reviewDate
      ? new Date(reviewDate)
          .toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric"
          })
          .toUpperCase()
      : ""

    // Get booking count - try multiple possible fields
    const bookingCount =
      videoReview.timesBooked ||
      videoReview.times_booked ||
      videoReview.client?.appointments?.filter(
        (appointment) => appointment.state === "approved" || appointment.state === 2
      )?.length ||
      0

    // Handle hover events for autoplay
    const handleMouseEnter = () => {
      if (videoRef.current) {
        videoRef.current.muted = true // Always ensure muted
        setIsMuted(true) // Update state to match
        videoRef.current.play()
      }
    }

    const handleMouseLeave = () => {
      if (videoRef.current) {
        videoRef.current.pause()
        videoRef.current.currentTime = 0 // Reset to beginning
      }
    }

    // Handle mute toggle
    const handleMuteToggle = (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (videoRef.current) {
        videoRef.current.muted = !videoRef.current.muted
        setIsMuted(videoRef.current.muted)
      }
    }

    return (
      <div
        key={index}
        className={twMerge("relative cursor-pointer overflow-hidden", getThemeShapeClasses(theme))}
        style={{ width: "12rem", height: "20rem" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}>
        {/* Booking Count Badge */}
        {bookingCount > 0 && (
          <div
            className="absolute left-2.5 top-2.5 z-10 bg-black px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow"
            style={{ borderRadius: "4px" }}>
            BOOKED {bookingCount} TIMES
          </div>
        )}

        {/* Video Player */}
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          src={videoReview.videoUrl || videoReview.video_url}
          poster={videoReview.thumbnailUrl}
          muted
          loop
          playsInline
          onLoadedMetadata={(e) => {
            // Set a default poster frame if no thumbnail
            if (!videoReview.thumbnailUrl) {
              e.target.currentTime = 1
            }
          }}
        />

        {/* Overlay Content */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

        {/* Bottom Content */}
        <div className="absolute inset-x-0 bottom-0 p-3 text-white">
          {/* Client Name */}
          <h3
            className="mb-1 text-[12px] font-normal"
            style={{
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
              color: "#FFFFFF"
            }}>
            {displayName}
          </h3>

          {/* Rating Stars */}
          {showRatings && (
            <div className="mb-1 flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <img
                  key={i}
                  src={i < rating ? "/images/email_mailer/star--5.png" : "/images/email_mailer/star--1.png"}
                  alt={i < rating ? "filled star" : "empty star"}
                  className="h-3 w-3"
                />
              ))}
            </div>
          )}

          {/* Date */}
          {showDates && formattedDate && (
            <p
              className="text-[10px] font-semibold uppercase tracking-wide"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: "#FFFFFF"
              }}>
              {formattedDate}
            </p>
          )}
        </div>

        {/* Mute/Unmute Button */}
        <button
          className={twMerge(
            "absolute bottom-4 right-4 flex h-5 w-5 items-center justify-center bg-black/50 text-white transition-colors hover:bg-black/70",
            getThemeShapeClasses(theme)
          )}
          onClick={handleMuteToggle}>
          <span className="text-xs font-bold">{isMuted ? "🔇" : "🔊"}</span>
        </button>
      </div>
    )
  }

  const TextReviewCard = ({ review, index, isExpanded = false }) => {
    const [expanded, setExpanded] = useState(isExpanded)

    // Get card styling based on layout
    const getCardClasses = () => {
      const baseClasses = twMerge(getThemeShapeClasses(theme))
      switch (layout) {
        case "cards":
          return twMerge("flex flex-col gap-4 border border-gray-200 bg-white px-6 py-4", baseClasses)
        case "reviews":
          return twMerge("flex flex-col gap-3 border border-gray-200 bg-white px-4 py-3", baseClasses)
        case "list":
          return twMerge("flex flex-row gap-3 border-b border-gray-200 bg-white px-3 py-2", baseClasses)
        default:
          return twMerge("flex flex-col gap-4 border border-gray-200 bg-white px-6 py-4", baseClasses)
      }
    }

    const reviewDate = review.createdAt
      ? new Date(review.createdAt)
          .toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
          })
          .toUpperCase()
      : ""

    // Get client name with anonymization
    const isAnonymous = review.isAnonymous
    const name = review.client?.firstName || review.user?.firstName || "Anonymous"
    const lastName = review.client?.lastName || review.user?.lastName
    const firstName = name && name.split(" ")[0]
    const lastInitial = (lastName && `${lastName[0]}.`?.toUpperCase()) || (name && name.split(" ")[1]?.toUpperCase())
    const displayName = isAnonymous ? "Anonymous" : `${firstName} ${lastInitial || ""}`.trim()

    // Get booking count - try multiple possible fields
    const bookingCount =
      review.times_booked ||
      review.timesBooked ||
      review.client?.appointments?.filter((appointment) => appointment.state === "approved" || appointment.state === 2)
        ?.length ||
      0

    // Review text handling - truncate after 4 lines
    const reviewText = review.review?.trim() || review.content?.trim() || ""

    // Function to truncate text to approximately 4 lines
    const truncateToLines = (text, maxLines = 4) => {
      if (!text) return ""

      // Split text into words
      const words = text.split(" ")
      const wordsPerLine = 10 // Approximate words per line on mobile (smaller)
      const maxWords = maxLines * wordsPerLine

      if (words.length <= maxWords) return text

      // Truncate to max words and add ellipsis
      return words.slice(0, maxWords).join(" ") + "..."
    }

    const shouldTruncate = reviewText.length > 200 // Keep original logic as fallback
    const displayText = expanded || !shouldTruncate ? reviewText : truncateToLines(reviewText)

    return (
      <div
        key={index}
        className={getCardClasses()}
        style={{
          borderColor: theme?.colors?.border || "#E6E6E6",
          maxWidth: "720px"
        }}>
        {/* Header Section */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            {/* Name and Stars */}
            <div className="flex flex-col gap-2">
              <h3
                className="text-[16px] font-semibold"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: theme?.colors?.textDark || "#000000"
                }}>
                {displayName}
              </h3>

              {showRatings && (
                <div className="flex items-center gap-[2px]">
                  {[...Array(5)].map((_, i) => (
                    <img
                      key={i}
                      src={
                        i < (review.rating || 0)
                          ? "/images/email_mailer/star--5.png"
                          : "/images/email_mailer/star--1.png"
                      }
                      alt={i < (review.rating || 0) ? "filled star" : "empty star"}
                      className="h-5 w-5"
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Date */}
            {showDates && reviewDate && (
              <span
                className="text-[10px] font-semibold uppercase tracking-wider"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  color: theme?.colors?.textDark || "#000000"
                }}>
                {reviewDate}
              </span>
            )}
          </div>

          {/* Review Text */}
          <div className="flex flex-col gap-3">
            <p
              className="text-[14px] leading-relaxed"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                color: theme?.colors?.textDark || "#000000"
              }}>
              {displayText}
              {shouldTruncate && (
                <>
                  {!expanded ? (
                    <button
                      onClick={() => setExpanded(true)}
                      className="ml-1 text-[12px] font-semibold underline"
                      style={{
                        color: theme?.colors?.primary || "#BD654F",
                        fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                      }}>
                      Show more
                    </button>
                  ) : (
                    <button
                      onClick={() => setExpanded(false)}
                      className="ml-1 text-[12px] font-semibold underline"
                      style={{
                        color: theme?.colors?.primary || "#BD654F",
                        fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                      }}>
                      Show less
                    </button>
                  )}
                </>
              )}
            </p>

            {/* Booking Count Badge */}
            {bookingCount > 0 && (
              <div
                className={twMerge(
                  "inline-flex w-fit items-center justify-center px-2 py-1 text-[10px] font-semibold uppercase tracking-wider",
                  getThemeShapeClasses(theme)
                )}
                style={{
                  backgroundColor: theme?.colors?.backgroundLight || "#F4ECE9",
                  color: theme?.colors?.textDark || "#000000",
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                }}>
                BOOKED {bookingCount} TIMES
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <section
      className={twMerge(
        "relative flex min-h-[400px] items-center justify-center py-12",
        isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
      )}
      style={{ backgroundColor }}>
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-col items-center gap-12">
          {/* Header Section - Only show if not on reviews page */}
          {!isOnReviewsPage && (
            <div className="flex flex-col items-center gap-4">
              {/* Verified Badge */}
              <div className="flex items-center gap-3">
                <div className="relative h-6 w-6">
                  <img src="/images/HealmeLogo.png" alt="Heal.me" className="h-full w-full object-contain" />
                  {/* Checkmark positioned at bottom right */}
                  <div
                    className={twMerge(
                      "absolute -bottom-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full shadow-sm",
                      getThemeShapeClasses(theme)
                    )}
                    style={{ backgroundColor: "#0D9DA4" }}>
                    <img src="/images/icons/verified-checkmark.svg" alt="verified" className="h-2 w-2" />
                  </div>
                </div>
                <div className={twMerge("flex items-center gap-1 px-0 py-1", getThemeShapeClasses(theme))}>
                  <a
                    href={window.location.origin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-semibold uppercase tracking-wider hover:underline"
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      color: getTextColor(theme?.colors?.textDark || "#000000")
                    }}>
                    Heal.me verified Reviews
                  </a>
                </div>
              </div>

              {/* Title */}
              <h2
                className="text-[28px] font-bold leading-[32px]"
                style={{
                  fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                  fontWeight: theme?.fonts?.headingWeight || "700",
                  color: getTextColor(theme?.colors?.textDark || "#55433F")
                }}>
                What clients are saying
              </h2>

              {/* Overall Rating */}
              {showOverallRating && (selectedTextReviews.length > 0 || selectedVideoReviews.length > 0) && (
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <img
                        key={i}
                        src={
                          i < parseFloat(averageRating)
                            ? "/images/email_mailer/star--5.png"
                            : "/images/email_mailer/star--1.png"
                        }
                        alt={i < parseFloat(averageRating) ? "filled star" : "empty star"}
                        className="h-5 w-5"
                      />
                    ))}
                  </div>
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider"
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      color: getTextColor(theme?.colors?.textDark || "#000000")
                    }}>
                    {allTextReviews.length + allVideoReviews.length} Reviews
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Top CTA Button - Only show if not on reviews page */}
          {showCTA && allReviews.length > 0 && !isOnReviewsPage && (
            <button
              onClick={() => (window.location.href = scopePath("/review", practice))}
              className={twMerge(
                "inline-flex items-center justify-center px-8 py-2 text-[14px] font-normal transition-all",
                getThemeShapeClasses(theme)
              )}
              style={{
                backgroundColor: theme?.colors?.primary || "#BD654F",
                color: "#F4ECE9",
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
              }}>
              {ctaText}
            </button>
          )}

          {/* Content Section */}
          <div className="flex flex-col gap-8">
            {/* Video Reviews Section */}
            {showVideoReviews && selectedVideoReviews.length > 0 && (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col items-center gap-4">
                  {selectedVideoReviews.map((videoReview, index) => (
                    <VideoReviewCard key={videoReview.id} videoReview={videoReview} index={index} />
                  ))}
                </div>
              </div>
            )}

            {/* Text Reviews Section */}
            {showTextReviews && selectedTextReviews.length > 0 && (
              <div className="flex flex-col items-center gap-6">
                <div
                  className={`w-full max-w-[720px] ${
                    layout === "cards"
                      ? "flex flex-col gap-3"
                      : layout === "reviews"
                      ? "flex flex-col gap-3"
                      : layout === "list"
                      ? "flex flex-col gap-2"
                      : "flex flex-col gap-3"
                  }`}>
                  {selectedTextReviews.map((review, index) => (
                    <TextReviewCard key={review.id} review={review} index={index} />
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {(!showVideoReviews || selectedVideoReviews.length === 0) &&
              (!showTextReviews || selectedTextReviews.length === 0) && (
                <div className="flex flex-col items-center gap-3 py-8 text-center">
                  <div className="text-4xl">⭐</div>
                  <h3
                    className="text-[20px] font-semibold"
                    style={{
                      fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                      color: getTextColor(theme?.colors?.textDark || "#000000")
                    }}>
                    No reviews yet
                  </h3>
                  <p
                    className="text-[14px] text-gray-600"
                    style={{
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                    }}>
                    Reviews will appear here once clients leave them
                  </p>
                </div>
              )}

            {/* Infinite Scroll Sentinel and Loading Indicator - Only show on reviews page */}
            {isOnReviewsPage && (
              <div className="flex w-full flex-col items-center justify-center py-8">
                {isLoading && (
                  <div className="flex items-center gap-3">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"></div>
                    <span
                      className="text-sm font-medium"
                      style={{
                        color: theme?.colors?.textDark || "#000000",
                        fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                      }}>
                      Loading more reviews...
                    </span>
                  </div>
                )}
                {/* {!hasMoreReviews && allReviews.length > 10 && (
                  <div className="flex w-full justify-center">
                    <span
                      className="text-lg font-medium"
                      style={{
                        color: theme?.colors?.textDark || "#000000",
                        fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                      }}>
                      this is all ✨
                    </span>
                  </div>
                )} */}
                {/* Sentinel element for infinite scroll */}
                <div ref={sentinelRef} className="h-1 w-full" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ReviewsSectionMobile
